import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { add_user } from '../api/usersApi'
import { toast } from 'react-toastify'
import { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import { AddUserInterface, RoleListInterface, addUserInitValues } from '../../../modules/accounts/components/settings/SettingsModel'
import * as Yup from 'yup'
import { get_active_roles } from '../../roles/api/rolesApi'
import { validateImage } from '../../../helper-functions/ImageValidation'
import { LoadingProgressBar } from '../../projects/components/LoadingProgressBar'

const userSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    role: Yup.string().required("Role is required"),
    email: Yup.string()
        .required('Email is required')
        .matches(/^[A-Za-z0-9._%+-]+@([A-Za-z0-9.-]+\.[A-Za-z]{2,4})$/, 'Invalid email address'),
})

function AddUser() {
    const [roleList, setRoleList] = useState<RoleListInterface[]>([]);
    const [loading, setLoading] = useState(false)
    const [userPicture, setUserPicture] = useState<File | null>(null);
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { data, isLoading: isRolesLoading } = useQuery('roles', get_active_roles, { refetchOnWindowFocus: false });
    
    useEffect(() => {
        if (data) {
            setRoleList(data.data)
        }
    }, [data]);

    const mutation = useMutation(add_user, {
        onSuccess: (data) => {
            toast.success(data.message || 'User added successfully', {
                position: 'top-right',
                autoClose: 3000,
            });
            navigate('/users/users-list');
            setLoading(false);
            queryClient.invalidateQueries('get_all_users');
        },
        onError: (error: any) => {
            const errorMessage = error.response?.data?.message || 'Something went wrong';
            toast.error(errorMessage, {
                position: 'top-right',
                autoClose: 3000,
            });
            setLoading(false);
        },
    });

    const formik = useFormik<AddUserInterface>({
        initialValues: addUserInitValues,
        validationSchema: userSchema,
        onSubmit: (values) => {
            setLoading(true)
            // Create a FormData object
            const formData = new FormData();
            if (values.title) {
                formData.append('title', values.title);
            }
            formData.append('name', values.name);
            formData.append('email', values.email);
            formData.append('role', values.role);
            if (userPicture) {
                formData.append('profile_picture', userPicture);
            }
            mutation.mutate(formData)
        },
    })

    const handleImageChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        logoType: string,
        MAX_FILE_SIZE: number,
        ALLOWED_TYPES: string[],
        MAX_DIMENSIONS: { [key: string]: { width: number; height: number } }
    ) => {
        const newFile = e.target.files?.[0];
        if (newFile) {
            // Call the validation function
            validateImage(
                newFile,
                logoType,
                (validFileUrl) => {
                    // If valid, update the state with the image URL
                    if (logoType === 'picture') {
                        setUserPicture(newFile);
                    }
                },
                MAX_FILE_SIZE, // File size limit in MB
                ALLOWED_TYPES, // Allowed file types
                MAX_DIMENSIONS // Maximum dimensions per logo type
            );
        }
    };

    return (
        <>
            {/* Loading Progress Bar */}
            <LoadingProgressBar isLoading={isRolesLoading || loading || mutation.isLoading} />
            
            <div className='card mb-5 mb-xl-10'>
                <div className='card-header border-0'>
                    <div className='card-title m-0'>
                        <h3 className='fw-bolder m-0'>Add User</h3>
                    </div>
                </div>
                <div id='kt_account_profile_details' className='collapse show'>
                    <form onSubmit={formik.handleSubmit} noValidate className='form'>
                        <div className='card-body border-top p-9'>
                            {/* Title Field */}
                            <div className='row mb-6'>
                                <label className='col-lg-4 col-form-label fw-bold fs-6'>
                                    Title
                                </label>
                                <div className='col-lg-8 fv-row'>
                                    <select
                                        className='form-select form-select-solid form-select-lg fw-bold'
                                        {...formik.getFieldProps('title')}
                                    >
                                        <option value=''>Select Title...</option>
                                        <option value='Mr.'>Mr.</option>
                                        <option value='Mrs.'>Mrs.</option>
                                        <option value='Ms.'>Ms.</option>
                                    </select>
                                </div>
                            </div>

                            {/* Name Field */}
                            <div className='row mb-6'>
                                <label className='col-lg-4 col-form-label required fw-bold fs-6'>Name</label>
                                <div className='col-lg-8 fv-row'>
                                    <input
                                        type='text'
                                        className='form-control form-control-lg form-control-solid'
                                        placeholder="Name"
                                        {...formik.getFieldProps('name')}
                                    />
                                    {formik.touched.name && formik.errors.name && (
                                        <div className='fv-plugins-message-container'>
                                            <div className='fv-help-block'>{formik.errors.name}</div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Email Field */}
                            <div className='row mb-6'>
                                <label className='col-lg-4 col-form-label fw-bold fs-6'>
                                    <span className='required'>Email</span>
                                </label>
                                <div className='col-lg-8 fv-row'>
                                    <input
                                        type='email'
                                        className='form-control form-control-lg form-control-solid'
                                        placeholder="Email"
                                        {...formik.getFieldProps('email')}
                                    />
                                    {formik.touched.email && formik.errors.email && (
                                        <div className='fv-plugins-message-container'>
                                            <div className='fv-help-block'>{formik.errors.email}</div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Role Field */}
                            <div className='row mb-6'>
                                <label className='col-lg-4 col-form-label fw-bold fs-6'>
                                    <span className='required'>Role</span>
                                </label>
                                <div className='col-lg-8 fv-row'>
                                    {isRolesLoading ? (
                                        <div className='placeholder placeholder-lg bg-light w-100'></div>
                                    ) : (
                                        <select
                                            className='form-select form-select-solid form-select-lg fw-bold'
                                            {...formik.getFieldProps('role')}
                                        >
                                            <option value=''>Select a Role...</option>
                                            {Array.isArray(roleList) && roleList.map((rolType) => (
                                                <option key={rolType.id} value={rolType.id}>
                                                    {rolType.name}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                    {formik.touched.role && formik.errors.role && (
                                        <div className='fv-plugins-message-container'>
                                            <div className='fv-help-block'>{formik.errors.role}</div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Profile Picture Field */}
                            <div className='row mb-6'>
                                <label className='col-lg-4 col-form-label fw-bold fs-6'>Picture</label>
                                <div className='col-lg-8 fv-row'>
                                    <input
                                        type='file'
                                        className='form-control form-control-lg form-control-solid'
                                        onChange={(e) => handleImageChange(
                                            e,
                                            'picture',
                                            2, // Max file size (2MB)
                                            ['image/jpeg', 'image/jpg', 'image/png'], // Allowed types
                                            {
                                                picture: { width: 200, height: 200 }
                                            } // Max dimensions
                                        )}
                                        accept="image/jpeg, image/jpg, image/png"
                                        name='picture'
                                    />
                                    <div className='text-danger mt-2'>
                                        Recommended size: 200 x 200 px, max file size: 2MB, and accepted file types: jpg, jpeg, or png only.
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='card-footer d-flex justify-content-end py-6 px-9'>
                            <button
                                data-toggle="tooltip" data-placement="bottom" title="Navigate to Users List"
                                className='btn btn-light-dark mx-3'
                                onClick={() => navigate('/users/users-list')}
                            >
                                Cancel
                            </button>
                            <button 
                                data-toggle="tooltip" 
                                data-placement="bottom" 
                                title="Add User" 
                                type='submit' 
                                className='btn btn-primary' 
                                disabled={loading || mutation.isLoading}
                            >
                                {!loading && !mutation.isLoading && 'Add User'}
                                {(loading || mutation.isLoading) && (
                                    <span className='indicator-progress' style={{ display: 'block' }}>
                                        Please wait...{' '}
                                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                    </span>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    )
}

export default AddUser
